<template>
  <div class="summary-card">
    <!-- Skeleton Loader -->
    <div v-if="loading" class="skeleton-container">
      <div class="summary-header">
        <div class="skeleton-text skeleton-title"></div>
        <div class="skeleton-period-selector">
          <div class="skeleton-period-btn" v-for="i in 3" :key="i"></div>
        </div>
      </div>

      <div class="skeleton-text skeleton-amount"></div>

      <div class="chart-container">
        <div class="skeleton-chart"></div>
      </div>
    </div>

    <!-- Actual Content -->
    <div v-else>
      <div class="summary-header">
        <h2 class="summary-title">Summary</h2>
        <div class="period-selector">
          <button 
            v-for="period in periods" 
            :key="period"
            :class="['period-btn', { active: selectedPeriod === period }]"
            @click="changePeriod(period)"
          >
            {{ period }}
          </button>
        </div>
      </div>

      <div class="summary-amount">IDR {{ amount }}</div>

      <div class="chart-container">
         <svg class="chart" viewBox="0 0 300 100" preserveAspectRatio="none">
          <path
            d="M0,80 C10,70 15,60 30,65 C45,70 55,40 70,30 C85,20 95,40 110,35 C125,30 135,45 150,40 C165,35 175,15 190,10 C205,5 215,25 230,20 C245,15 255,25 270,15 C285,5 295,10 300,5"
            fill="none"
            stroke="#4B9CD3"
            stroke-width="2"
          />
          
          <circle v-for="(dot, index) in dots" :key="index" :cx="dot.x" :cy="dot.y" r="3" fill="#4B9CD3" />
        </svg>
        <!-- <img src="/assets/chart-graphic.png" alt="" class="chart-image"> -->
      </div>
    </div>
  </div>
</template>

<script>
import { getSummary } from '@/api/company.js';
import { formatNumber } from '@/utils/param-utils.js';

export default {
  name: 'SummaryChart',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedPeriod: 'Today',
      periods: ['Today', 'This Month', 'This Year'],
      dots: [
        { x: 30, y: 65 },
        { x: 70, y: 30 },
        { x: 110, y: 35 },
        { x: 150, y: 40 },
        { x: 190, y: 10 },
        { x: 230, y: 20 },
        { x: 270, y: 15 },
        { x: 300, y: 5 }
      ],
      amount: 0
    }
  },
  methods : {
    async fetchSummary(){
      if (this.loading) return; // Don't fetch if parent is still loading
      
      const period = this.selectedPeriod === 'Today' ? 'today' : this.selectedPeriod === 'This Month' ? 'month' : 'year';
      const params = {
        type : period
      }

      try {
        const response = await getSummary(params);  
        this.amount = formatNumber(response.data.grand_total_amount);
      } catch (error) {
        console.error('Error fetching summary:', error);
        this.amount = 0;
      }
    },
    changePeriod(period) {
      if (this.loading) return; // Don't allow period change while loading
      this.selectedPeriod = period;
      this.fetchSummary();
    }
  }, 
  watch: {
    loading(newVal, oldVal) {
      // When loading changes from true to false, fetch data
      if (oldVal === true && newVal === false) {
        this.fetchSummary();
      }
    }
  },
  mounted() {
    if (!this.loading) {
      this.fetchSummary();
    }
  }
}
</script>

<style scoped>
.summary-card {
  background: linear-gradient(to bottom, #E9FBF0, #FFFFFF);
  border-radius: 16px;
  padding: 24px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.summary-title {
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: #333;
}

.period-selector {
  display: flex;
  background-color: #CFECDB;
  border-radius: 50px;
  padding: 3px;
}

.period-btn {
  background: none;
  border: none;
  padding: 6px 12px;
  font-size: 10px;
  border-radius: 50px;
  cursor: pointer;
  color: #7C8C8D;
}

.period-btn.active {
  background-color: #fff;
  color: #000;
}

.summary-amount {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
}

.chart-container {
  height: 100px;
  position: relative;
  overflow: hidden;
}

.chart-image {
  width: 100%; 
  height: 100%;
  object-fit: cover; 
  position: absolute;
  top: 0;
  left: 0;
}

.chart {
  width: 100%;
  height: 100%;
}

/* Skeleton Loader Styles */
.skeleton-container {
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-text,
.skeleton-period-btn,
.skeleton-chart {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-title {
  width: 80px;
  height: 22px;
}

.skeleton-period-selector {
  display: flex;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 50px;
  padding: 3px;
  gap: 2px;
}

.skeleton-period-btn {
  width: 45px;
  height: 24px;
  border-radius: 50px;
}

.skeleton-amount {
  width: 150px;
  height: 28px;
  margin-bottom: 20px;
}

.skeleton-chart {
  width: 100%;
  height: 100px;
  border-radius: 12px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}
</style>